// ageGenderTargeting.js — AS-117
// Utilities for building PAC payload and parsing AdGroup response for
// Age / Gender targeting (include-only, no mode).

export const AGE_ALL_VALUES = ['0017', '1824', '2534', '3544', '4554', '5564', '6574', '7599', 'unknown'];
export const GENDER_ALL_VALUES = ['male', 'female', 'unknown'];

/**
 * Default state: all = true (no restriction).
 * State shape: { all: bool, selected: string[] }  (no mode field)
 */
export function makeDefaultAgeGenderState() {
  return { all: true, selected: [] };
}

/**
 * Build the PAC-compatible fields for age or gender.
 * Returns { all, selected } with selected empty when all=true.
 */
export function buildAgeGenderDimension(state) {
  return {
    all:      state.all,
    selected: state.all ? [] : (state.selected || []),
  };
}

/**
 * Parse age state from an AdGroup response.
 * Handles both new schema (top-level age_all present) and v2 fallback (ts2.age_tag_acl).
 *
 * @param {object} data  — raw AdGroup data (top-level fields)
 * @param {object} ts2   — parsed targetingSettings_v2 object (may be null)
 * @returns {{ all, selected }}
 */
export function parseAgeFromAdGroup(data, ts2) {
  if (data.age_all !== undefined) {
    // New schema
    return {
      all:      !!data.age_all,
      selected: data.age_tag || [],
    };
  }
  // v2 fallback: age_tag_acl { allow_all?, whitelist }
  const v2 = ts2 && ts2.age_tag_acl;
  if (v2) {
    if (v2.allow_all === false) {
      return { all: false, selected: v2.whitelist || [] };
    }
    if ((v2.whitelist || []).length > 0) {
      return { all: false, selected: v2.whitelist };
    }
  }
  return makeDefaultAgeGenderState();
}

/**
 * Parse gender state from an AdGroup response.
 * Handles both new schema (top-level gender_all present) and v2 fallback (ts2.gender_tag_acl).
 *
 * @param {object} data  — raw AdGroup data (top-level fields)
 * @param {object} ts2   — parsed targetingSettings_v2 object (may be null)
 * @returns {{ all, selected }}
 */
export function parseGenderFromAdGroup(data, ts2) {
  if (data.gender_all !== undefined) {
    // New schema
    return {
      all:      !!data.gender_all,
      selected: data.gender_tag || [],
    };
  }
  // v2 fallback: gender_tag_acl { allow_all?, whitelist }
  const v2 = ts2 && ts2.gender_tag_acl;
  if (v2) {
    if (v2.allow_all === false) {
      return { all: false, selected: v2.whitelist || [] };
    }
    if ((v2.whitelist || []).length > 0) {
      return { all: false, selected: v2.whitelist };
    }
  }
  return makeDefaultAgeGenderState();
}

/**
 * Validate age and gender states before submit.
 * Returns an error message string or null if valid.
 */
export function validateAgeGenderStates(ageState, genderState) {
  if (!ageState.all && ageState.selected.length === 0) {
    return '請選擇至少一個年齡範圍，或勾選「全部年齡」';
  }
  if (!genderState.all && genderState.selected.length === 0) {
    return '請選擇至少一個性別，或勾選「全部性別」';
  }
  return null;
}
